import { useMutation } from '@tanstack/react-query';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface ManualDealInputs {
  // Property basics
  address: string;
  market_id: string;
  total_units: number;
  livable_area: number | null;
  year_built: number | null;

  // Financial
  purchase_price: number;
  rent_per_unit: number;
  capex: number;
  vacancy_rate: number;
  interest_rate: number | null;

  // Deal type
  financing_type: 'conventional' | 'seller' | 'hard_money' | 'cash';
}

interface StandaloneRequest {
  context: {
    property_id: string;
    address: string;
    total_units: number;
    livable_area: number | null;
    year_built: number | null;
    neighborhood: string | null;
    tier: string;
    market_value: number | null;
  };
  inputs: {
    market_id: string;
    purchase_price: number;
    rent_per_unit: number;
    capex: number;
    vacancy_rate: number;
    interest_rate_override: number | null;
    total_units: number;
    financing_type: string;
    hold_period_years: number;
    exit_cap: number;
    rent_growth: number;
  };
}

function buildRequest(deal: ManualDealInputs): StandaloneRequest {
  const slug = deal.address
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return {
    context: {
      property_id: `manual-${slug}`,
      address: deal.address,
      total_units: deal.total_units,
      livable_area: deal.livable_area,
      year_built: deal.year_built,
      neighborhood: null,
      tier: 'Tier2',
      market_value: deal.purchase_price,
    },
    inputs: {
      market_id: deal.market_id,
      purchase_price: deal.purchase_price,
      rent_per_unit: deal.rent_per_unit,
      capex: deal.capex,
      vacancy_rate: deal.vacancy_rate,
      interest_rate_override: deal.interest_rate,
      total_units: deal.total_units,
      financing_type: deal.financing_type,
      hold_period_years: 5,
      exit_cap: 6.5,
      rent_growth: 3.0,
    },
  };
}

async function runStandaloneAnalysis(deal: ManualDealInputs) {
  const payload = buildRequest(deal);

  const response = await fetch(
    `${API_BASE}/api/calculations/standalone-analysis?context=${encodeURIComponent(JSON.stringify(payload.context))}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload.inputs),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Analysis failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export function useStandaloneAnalysis() {
  return useMutation({
    mutationFn: runStandaloneAnalysis,
  });
}
